import type { ReactElement } from "react"
import { addDays, addHours, addMinutes, addSeconds } from "date-fns/fp"
import { startOfMonth, getDaysInMonth, getDay } from "date-fns"
import { popDialog } from "@/store/dialogs-state"
import { Button } from "../ui/Button"
import { Dialog } from "../ui/Dialog"
import { PhotoRecord } from "@/lib/catalog"
import { metadataAtom } from "@/store/photo/photos-state"
import { useAtom } from "jotai"
import { useState, useEffect, useRef, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { adjustCaptureTime } from "@/store/photo/store-photos"
import { Icon } from "../icons/Icon"
import { formatCaptureTime } from "@/utils/date"

const parseInputValue = (input: string) => {
	const result = parseInt(input)
	if (isNaN(result)) {
		return 0
	} else {
		return result
	}
}

export const AdjustTimeDialog = (props: { anchorPhoto: PhotoRecord }) => {
	const { t } = useTranslation("components")
	const [metadata] = useAtom(metadataAtom)
	const currentMetadata = metadata[props.anchorPhoto.id]

	const currentTime = currentMetadata?.dateCreated ?? new Date().getTime()
	const captureOffsetMinutes = currentMetadata?.dateCreatedOffset
	const currentDate = new Date(currentTime)

	const currentDisplayTime = () => formatCaptureTime(currentTime, captureOffsetMinutes)

	const newDisplayTime = () => formatCaptureTime(currentTime + totalOffset, captureOffsetMinutes)

	const [dayOffset, setDayOffset] = useState(0)
	const [hourOffset, setHourOffset] = useState(0)
	const [minOffset, setMinOffset] = useState(0)
	const [secOffset, setSecOffset] = useState(0)

	const [newDate] = [currentDate]
		.map(addDays(dayOffset))
		.map(addHours(hourOffset))
		.map(addMinutes(minOffset))
		.map(addSeconds(secOffset))
	const totalOffset = newDate ? newDate.getTime() - currentTime : 0

	return (
		<Dialog trackingViewName="adjust_time_dialog" contentSize="sm-h-auto" disableClose>
			<div className="flex w-full flex-col items-start justify-start space-y-4">
				<div className="text-start text-md font-bold">{t("adjustTimeDialog.title")}</div>
				<div className="flex w-full flex-row items-center space-x-3 text-sm">
					<div className="flex flex-row items-center space-x-1">
						<input
							className="h-6 w-12 rounded-md px-1"
							type="number"
							value={dayOffset}
							onChange={(e) => setDayOffset(parseInputValue(e.target.value))}
						/>
						<div>{t("adjustTimeDialog.day")}</div>
					</div>
					<div className="flex flex-row items-center space-x-1">
						<input
							className="h-6 w-12 rounded-md px-1"
							type="number"
							value={hourOffset}
							onChange={(e) => setHourOffset(parseInputValue(e.target.value))}
						/>
						<div>{t("adjustTimeDialog.hour")}</div>
					</div>
					<div className="flex flex-row items-center space-x-1">
						<input
							className="h-6 w-12 rounded-md px-1"
							type="number"
							value={minOffset}
							onChange={(e) => setMinOffset(parseInputValue(e.target.value))}
						/>

						<div>{t("adjustTimeDialog.min")}</div>
					</div>
					<div className="flex flex-row items-center space-x-1">
						<input
							className="h-6 w-12 rounded-md px-1"
							type="number"
							value={secOffset}
							onChange={(e) => setSecOffset(parseInputValue(e.target.value))}
						/>
						<div>{t("adjustTimeDialog.sec")}</div>
					</div>
				</div>
				<div className="flex flex-row space-x-4">
					<div className="flex flex-col">
						<div className="text-sm text-base-400">
							{t("adjustTimeDialog.currentTime")}
						</div>
						<div className="text-sm text-base-400">{t("adjustTimeDialog.newTime")}</div>
					</div>

					<div className="flex flex-col">
						<div className="text-sm text-base-400">{currentDisplayTime()}</div>
						<div className="text-sm text-base-400">{newDisplayTime()}</div>
					</div>
				</div>

				<div className="flex w-full flex-1 flex-row items-center justify-between space-x-2">
					<div className="text-sm text-base-400">
						{t("adjustTimeDialog.cannotBeUndone")}
					</div>
					<div className="flex flex-row space-x-2">
						<Button trackingLabel="cancel" onClick={() => popDialog()}>
							{t("common:action.cancel")}
						</Button>
						<Button
							trackingLabel="change"
							disabled={isNaN(totalOffset)}
							onClick={() => {
								adjustCaptureTime(totalOffset)
								popDialog()
							}}
							variant="primary"
						>
							{t("adjustTimeDialog.change")}
						</Button>
					</div>
				</div>
			</div>
		</Dialog>
	)
}

// Calendar Picker Component
const CalendarPicker = (props: {
	selectedDate: Date
	onDateChange: (date: Date) => void
	onClose: () => void
}) => {
	const { i18n } = useTranslation()
	const locale = i18n.language
	const [viewDate, setViewDate] = useState(props.selectedDate)
	const [view, setView] = useState<"days" | "months" | "years">("days")
	const containerRef = useRef<HTMLDivElement>(null)
	const yearsRef = useRef<HTMLDivElement>(null)

	const startYear = 1900
	const endYear = 2100
	const yearHeight = 48
	const yearGapSize = 8
	const yearsContainerHeight = 252

	const monthNames = useMemo(
		() =>
			Array.from({ length: 12 }, (_, i) =>
				new Intl.DateTimeFormat(locale, { month: "long" }).format(new Date(2024, i)),
			),
		[locale],
	)

	const monthNamesShort = useMemo(
		() =>
			Array.from({ length: 12 }, (_, i) =>
				new Intl.DateTimeFormat(locale, { month: "short" }).format(new Date(2024, i)),
			),
		[locale],
	)

	// 0=Sun, 1=Mon ... 6=Sat (JS Date convention)
	const weekStartDay = useMemo(() => {
		const loc = new Intl.Locale(locale)
		const firstDay = loc.getWeekInfo?.()?.firstDay ?? loc.weekInfo?.firstDay
		if (firstDay) return firstDay === 7 ? 0 : firstDay
		return 0
	}, [locale])

	const dayNames = useMemo(
		() =>
			Array.from({ length: 7 }, (_, i) =>
				new Intl.DateTimeFormat(locale, { weekday: "narrow" }).format(
					new Date(2024, 6, 7 + ((i + weekStartDay) % 7)),
				),
			),
		[locale, weekStartDay],
	)

	const changeMonth = (offset: number) => {
		const newDate = new Date(viewDate)
		newDate.setMonth(newDate.getMonth() + offset)
		setViewDate(newDate)
	}

	useEffect(() => {
		if (view === "years" && yearsRef.current) {
			const selectedYearIndex = props.selectedDate.getFullYear() - startYear
			const scrollPosition =
				Math.floor(selectedYearIndex / 3) * (yearHeight + yearGapSize) -
				yearsContainerHeight / 2 +
				yearHeight / 2
			yearsRef.current.scrollTop = scrollPosition
		}
	}, [view, props.selectedDate])

	// Use native event listener to handle wheel with preventDefault
	useEffect(() => {
		const container = containerRef.current
		if (!container) return

		const handleWheel = (e: WheelEvent) => {
			e.preventDefault()
			if (view === "days") {
				if (e.deltaY > 0) {
					changeMonth(1)
				} else {
					changeMonth(-1)
				}
			}
		}

		container.addEventListener("wheel", handleWheel, { passive: false })
		return () => {
			container.removeEventListener("wheel", handleWheel)
		}
	}, [view, viewDate])

	const renderDays = () => {
		const year = viewDate.getFullYear()
		const month = viewDate.getMonth()
		const firstDay = startOfMonth(viewDate)
		const daysInMonth = getDaysInMonth(viewDate)
		const startingDayOfWeek = (getDay(firstDay) - weekStartDay + 7) % 7

		const days: ReactElement[] = []

		// Empty cells for days before the first day of the month
		for (let i = 0; i < startingDayOfWeek; i++) {
			const prevMonthDate = new Date(year, month, -startingDayOfWeek + i + 1)
			days.push(
				<Button
					key={`prev-${i}`}
					trackingLabel="select-prev-month-day"
					variant="ghost"
					size="custom"
					className="size-8 text-sm text-base-500"
					onClick={() => {
						const newDate = new Date(year, month - 1, prevMonthDate.getDate())
						props.onDateChange(newDate)
						setViewDate(newDate)
					}}
				>
					{prevMonthDate.getDate()}
				</Button>,
			)
		}

		// Days of the current month
		for (let day = 1; day <= daysInMonth; day++) {
			const date = new Date(year, month, day)
			const isSelected =
				props.selectedDate.getDate() === day &&
				props.selectedDate.getMonth() === month &&
				props.selectedDate.getFullYear() === year

			days.push(
				<Button
					key={day}
					trackingLabel="select-day"
					variant="ghost"
					size="custom"
					className={`size-8 text-sm ${
						isSelected ? "text-accent-500" : ""
					} hover:bg-base-100/10`}
					onClick={() => {
						props.onDateChange(date)
					}}
				>
					{day}
				</Button>,
			)
		}

		// Fill remaining cells to make exactly 6 rows (42 cells total)
		const totalCells = 42
		const remainingCells = totalCells - days.length
		for (let i = 1; i <= remainingCells; i++) {
			days.push(
				<Button
					key={`next-${i}`}
					trackingLabel="select-next-month-day"
					variant="ghost"
					size="custom"
					className="size-8 text-sm text-base-500"
					onClick={() => {
						const newDate = new Date(year, month + 1, i)
						props.onDateChange(newDate)
						setViewDate(newDate)
					}}
				>
					{i}
				</Button>,
			)
		}

		return days
	}

	const renderMonths = () => {
		return monthNamesShort.map((month, index) => {
			const isSelected =
				props.selectedDate.getMonth() === index &&
				props.selectedDate.getFullYear() === viewDate.getFullYear()

			return (
				<Button
					key={month}
					trackingLabel="select-month"
					variant="ghost"
					size="custom"
					className={`h-12 text-md ${
						isSelected ? "text-accent-500" : ""
					} hover:bg-base-100/10`}
					onClick={() => {
						const newDate = new Date(
							viewDate.getFullYear(),
							index,
							props.selectedDate.getDate(),
						)
						props.onDateChange(newDate)
						setViewDate(newDate)
						setView("days")
					}}
				>
					{month}
				</Button>
			)
		})
	}

	const renderYears = () => {
		const years: ReactElement[] = []

		for (let year = startYear; year <= endYear; year++) {
			const isSelected = props.selectedDate.getFullYear() === year

			years.push(
				<Button
					key={year}
					trackingLabel="select-year"
					variant="ghost"
					size="custom"
					className={`text-md ${
						isSelected ? "text-accent-500" : ""
					} hover:bg-base-100/10`}
					style={{ height: `${yearHeight}px` }}
					onClick={() => {
						const newDate = new Date(
							year,
							props.selectedDate.getMonth(),
							props.selectedDate.getDate(),
						)
						props.onDateChange(newDate)
						setViewDate(newDate)
						setView("days")
					}}
				>
					{year}
				</Button>,
			)
		}

		return (
			<div
				className="w-auto overflow-y-auto pb-5 pt-4"
				style={{ height: `${yearsContainerHeight}px` }}
				ref={yearsRef}
			>
				{" "}
				<div className="grid grid-cols-3" style={{ gap: `${yearGapSize}px` }}>
					{years}
				</div>
			</div>
		)
	}

	return (
		<div ref={containerRef} className="mt-2 w-full rounded-lg bg-base-800 p-4">
			{/* Header */}
			<div className="mb-4 grid grid-cols-3 gap-2">
				<Button
					trackingLabel="toggle-month-view"
					variant="text"
					size="custom"
					className="p-2 text-md text-base-100 hover:bg-base-100/10"
					onClick={() => setView(view === "months" ? "days" : "months")}
				>
					{monthNames[viewDate.getMonth()]}
				</Button>
				<Button
					trackingLabel="toggle-year-view"
					variant="text"
					size="custom"
					className="p-2 text-md text-base-100 hover:bg-base-100/10"
					onClick={() => setView(view === "years" ? "days" : "years")}
				>
					{viewDate.getFullYear()}
				</Button>
				<div className="mr-4 flex items-center justify-end space-x-4">
					{view === "days" && (
						<>
							<Button
								trackingLabel="prev-month"
								variant="text"
								size="custom"
								className="p-2 hover:bg-base-100/10"
								onClick={() => changeMonth(-1)}
							>
								<Icon name="arrow_back" size={20} />
							</Button>
							<Button
								trackingLabel="next-month"
								variant="text"
								size="custom"
								className="p-2 hover:bg-base-100/10"
								onClick={() => changeMonth(1)}
							>
								<Icon name="arrow_forward" size={20} />
							</Button>
						</>
					)}
				</div>
			</div>

			{/* Content */}
			{view === "days" && (
				<div>
					{/* Day names */}
					<div className="mb-2 grid grid-cols-7 gap-1">
						{dayNames.map((day, i) => (
							<div
								key={i}
								className="flex size-8 items-center justify-center text-sm text-base-400"
							>
								{day}
							</div>
						))}
					</div>
					{/* Calendar days */}
					<div className="grid grid-cols-7 gap-1">{renderDays()}</div>
				</div>
			)}

			{view === "months" && (
				<div className="grid grid-cols-3 gap-2 pb-5 pt-4">{renderMonths()}</div>
			)}

			{view === "years" && renderYears()}
		</div>
	)
}

export const AdjustDateDialog = (props: {
	currentDate: Date
	handler: (offset: number) => void
}) => {
	const { t, i18n } = useTranslation("components")
	const locale = i18n.language
	const formatDate = (date: Date) =>
		date.toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" })
	const currentTime = props.currentDate.getTime()
	const [selectedDate, setSelectedDate] = useState(props.currentDate)
	// i18next-instrument-ignore-next-line
	const [inputValue, setInputValue] = useState(formatDate(props.currentDate))
	const [showCalendar, setShowCalendar] = useState(false)

	const totalOffset = selectedDate.getTime() - currentTime

	const handleInputChange = (value: string) => {
		setInputValue(value)
		// Try to parse the input
		const parsed = new Date(value)
		if (!isNaN(parsed.getTime())) {
			setSelectedDate(parsed)
		}
	}

	const handleDateChange = (date: Date) => {
		setSelectedDate(date)
		setInputValue(formatDate(date))
	}

	return (
		<Dialog trackingViewName="adjust_date_dialog" contentSize="sm-h-auto" disableClose>
			<div className="flex w-full flex-col items-start justify-start space-y-4">
				<div className="text-start text-md font-bold text-base-100">
					{t("adjustTimeDialog.editProjectDate")}
				</div>

				{/* Date Input */}
				{/* i18next-instrument-ignore-next-line */}
				<input
					type="text"
					value={inputValue}
					onChange={(e) => handleInputChange(e.target.value)}
					onClick={() => {
						if (!showCalendar) {
							setShowCalendar(true)
						}
					}}
					className="w-full cursor-pointer rounded bg-base-700 px-3 py-2 text-sm text-base-100 transition-colors hover:bg-base-600"
					placeholder={t("adjustTimeDialog.mmmDYyyy")} // i18n-ignore: date format pattern
				/>

				{/* Calendar Picker */}
				{showCalendar && (
					<CalendarPicker
						selectedDate={selectedDate}
						onDateChange={handleDateChange}
						onClose={() => setShowCalendar(false)}
					/>
				)}

				{/* Action Buttons */}
				<div className="flex w-full flex-row items-center justify-end space-x-2 pt-2">
					<Button trackingLabel="cancel" onClick={() => popDialog()}>
						{t("common:action.cancel")}
					</Button>
					<Button
						trackingLabel="change"
						disabled={isNaN(totalOffset)}
						onClick={() => {
							props.handler(totalOffset)
							popDialog()
						}}
						variant="primary"
					>
						{t("adjustTimeDialog.change")}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
